import React from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Tabs,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedTab as Tab } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { AutoAwesome, Lock, Public, Schedule, SwapHoriz } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useChart } from '../contexts/ChartContext';
import { hasPremiumSubscription } from '../utils/subscription';
import CurrentTransit from './CurrentTransit';
import NakshatraTransit from '../components/transit/NakshatraTransit';
import DoubleTransitPanel from '../components/transit/DoubleTransitPanel';
import { formatDisplayDateTime } from '../utils/dateDisplay';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

type PersonalImpact = {
  locked?: boolean;
  available?: boolean;
  message?: string;
  summary?: string;
  moonSummary?: string | null;
  chartName?: string | null;
};

type TransitEvent = {
  _id: string;
  title: string;
  eventDate: string;
  shortDescription: string;
  detailedDescription?: string;
  isMajor: boolean;
  planet: string;
  eventType: string;
  toSign?: string | null;
  toNakshatra?: string | null;
  personalImpact?: PersonalImpact;
};

type TodayPayload = {
  today: {
    moonSign: string | null;
    moonNakshatra: string | null;
    sunSign: string | null;
    rahuKetuAxis: string | null;
    importantYoga: string | null;
  };
  events: TransitEvent[];
};

const HI_PLANETS: Record<string, string> = {
  Sun: 'सूर्य', Moon: 'चंद्र', Mars: 'मंगल', Mercury: 'बुध', Jupiter: 'गुरु', Venus: 'शुक्र', Saturn: 'शनि', Rahu: 'राहु', Ketu: 'केतु'
};

const HI_SIGNS: Record<string, string> = {
  Aries: 'मेष', Taurus: 'वृषभ', Gemini: 'मिथुन', Cancer: 'कर्क', Leo: 'सिंह', Virgo: 'कन्या', Libra: 'तुला', Scorpio: 'वृश्चिक', Sagittarius: 'धनु', Capricorn: 'मकर', Aquarius: 'कुंभ', Pisces: 'मीन',
  Mesha: 'मेष', Vrishabha: 'वृषभ', Mithuna: 'मिथुन', Karka: 'कर्क', Simha: 'सिंह', Kanya: 'कन्या', Tula: 'तुला', Vrischika: 'वृश्चिक', Dhanu: 'धनु', Makara: 'मकर', Kumbha: 'कुंभ', Meena: 'मीन'
};

const HI_NAKSHATRAS: Record<string, string> = {
  Ashwini: 'अश्विनी', Bharani: 'भरणी', Krittika: 'कृत्तिका', Rohini: 'रोहिणी', Mrigashira: 'मृगशीर्षा', Ardra: 'आर्द्रा', Punarvasu: 'पुनर्वसु', Pushya: 'पुष्य', Ashlesha: 'आश्लेषा', Magha: 'मघा', 'Purva Phalguni': 'पूर्वा फाल्गुनी', 'Uttara Phalguni': 'उत्तरा फाल्गुनी', Hasta: 'हस्त', Chitra: 'चित्रा', Swati: 'स्वाती', Vishakha: 'विशाखा', Anuradha: 'अनुराधा', Jyeshtha: 'ज्येष्ठा', Mula: 'मूल', 'Purva Ashadha': 'पूर्वाषाढ़ा', 'Uttara Ashadha': 'उत्तराषाढ़ा', Shravana: 'श्रवण', Dhanishta: 'धनिष्ठा', Shatabhisha: 'शतभिषा', 'Purva Bhadrapada': 'पूर्वा भाद्रपदा', 'Uttara Bhadrapada': 'उत्तरा भाद्रपदा', Revati: 'रेवती'
};

const localizeTransitValue = (value: string | null | undefined, isHindi: boolean) => {
  if (!isHindi || !value) return value || '—';
  return String(value)
    .split(/\s*\/\s*/)
    .map((part) => HI_PLANETS[part] || HI_SIGNS[part] || HI_NAKSHATRAS[part] || part)
    .join(' / ');
};

const localizeTransitTitle = (event: TransitEvent, isHindi: boolean) => {
  if (!isHindi) return event.title;
  const planet = localizeTransitValue(event.planet, true);
  if (event.eventType === 'NAKSHATRA_CHANGE') {
    return `${planet} ${localizeTransitValue(event.toNakshatra, true)} नक्षत्र में प्रवेश`;
  }
  return `${planet} ${localizeTransitValue(event.toSign, true)} राशि में प्रवेश`;
};

const localizeTransitDescription = (event: TransitEvent, isHindi: boolean) => {
  if (!isHindi) return event.shortDescription;
  const planet = localizeTransitValue(event.planet, true);
  if (event.eventType === 'NAKSHATRA_CHANGE') {
    return `${planet} ${localizeTransitValue(event.toNakshatra, true)} नक्षत्र में प्रवेश कर रहा है। मन, दिनचर्या और निर्णयों पर इसका प्रभाव देखें।`;
  }
  return `${planet} ${localizeTransitValue(event.toSign, true)} राशि में प्रवेश कर रहा है। गोचर प्रभाव और महत्वपूर्ण संकेत देखें।`;
};

const localizeImpactText = (value: string | null | undefined, isHindi: boolean) => {
  if (!value) return '';
  if (!isHindi) return value;
  const lagna = value.match(/affect your (\d+)(?:st|nd|rd|th) house from Lagna/i);
  if (lagna) return `यह गोचर आपके लग्न से ${lagna[1]}वें भाव को सक्रिय करेगा। व्यक्तिगत प्रभाव देखें।`;
  const moon = value.match(/From your Moon sign, it activates the (\d+)(?:st|nd|rd|th) house/i);
  if (moon) return `चंद्र राशि से यह ${moon[1]}वें भाव को सक्रिय करता है। मन और दैनिक निर्णयों पर असर देखें।`;
  return value
    .replace('Login to see transit impact for your chart.', 'अपनी कुंडली पर गोचर प्रभाव देखने के लिए लॉगिन करें।')
    .replace('Choose Plan to see how this transit affects your chart.', 'यह गोचर आपकी कुंडली को कैसे प्रभावित करेगा, देखने के लिए प्रीमियम लें।')
    .replace('Select a saved Kundli or add your birth details in profile to unlock personalized Gochar impact.', 'व्यक्तिगत गोचर प्रभाव देखने के लिए सेव कुंडली चुनें या प्रोफाइल में जन्म विवरण जोड़ें।');
};

const GocharAlerts: React.FC = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { state: authState } = useAuth();
  const { currentChart } = useChart();
  const isPremium = hasPremiumSubscription(authState.user);
  const [tab, setTab] = React.useState<'daily' | 'upcoming' | 'double' | 'nakshatra' | 'manual'>('daily');
  const [todayData, setTodayData] = React.useState<TodayPayload | null>(null);
  const [upcoming, setUpcoming] = React.useState<TransitEvent[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    document.title = `${t('transit.pageTitle')} - Kundlistar`;
  }, [t]);

  React.useEffect(() => {
    const headers = authState.token
      ? { Authorization: `Bearer ${authState.token}` }
      : undefined;
    const selectedChartId = id || currentChart?._id || currentChart?.chartId;
    const query = selectedChartId ? `?chartId=${encodeURIComponent(selectedChartId)}` : '';

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [todayResponse, upcomingResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/transits/today${query}`, { headers, timeout: 30000 }),
          axios.get(`${API_BASE_URL}/transits/upcoming${query ? `${query}&days=30` : '?days=30'}`, { headers, timeout: 120000 })
        ]);
        setTodayData(todayResponse.data.data);
        setUpcoming(upcomingResponse.data.data || []);
      } catch (err: any) {
        setError(err.code === 'ECONNABORTED'
          ? 'Transit data took too long to load. Please try again.'
          : err.response?.data?.message || t('transit.loadError'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authState.token, currentChart?._id, currentChart?.chartId, id, t]);

  const isHindi = Boolean(i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi'));
  const formatDate = (value: string) => formatDisplayDateTime(value);

  const featuredImpact = upcoming.find((event) => event.personalImpact)?.personalImpact
    || todayData?.events.find((event) => event.personalImpact)?.personalImpact
    || null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {t('transit.pageTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
            {t('transit.pageSubtitle')}
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Tabs
            value={tab}
            onChange={(_event, nextValue) => setTab(nextValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 0,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 999,
              },
            }}
          >
            <Tab
              value="daily"
              icon={<Public fontSize="small" />}
              iconPosition="start"
              label={t('transit.tabs.daily')}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
            <Tab
              value="upcoming"
              icon={<Schedule fontSize="small" />}
              iconPosition="start"
              label={t('transit.tabs.upcoming')}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
            <Tab
              value="double"
              icon={<SwapHoriz fontSize="small" />}
              iconPosition="start"
              label={t('doubleTransit.title')}
              sx={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }}
            />
            <Tab
              value="nakshatra"
              icon={<AutoAwesome fontSize="small" />}
              iconPosition="start"
              label="Nakshatra Transit"
              sx={{ textTransform: 'none', fontWeight: 700, minHeight: 48 }}
            />
            <Tab
              value="manual"
              icon={<SwapHoriz fontSize="small" />}
              iconPosition="start"
              label={t('transit.tabs.manual')}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
          </Tabs>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : tab === 'daily' ? (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Public color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('transit.today.title')}
                    </Typography>
                  </Stack>

                  <Grid container spacing={2}>
                    {[
                      [t('transit.today.moonIn'), localizeTransitValue(todayData?.today.moonSign, isHindi)],
                      [t('transit.today.moonNakshatra'), localizeTransitValue(todayData?.today.moonNakshatra, isHindi)],
                      [t('transit.today.sunIn'), localizeTransitValue(todayData?.today.sunSign, isHindi)],
                      [t('transit.today.rahuKetuAxis'), localizeTransitValue(todayData?.today.rahuKetuAxis, isHindi)],
                      [t('transit.today.importantYoga'), todayData?.today.importantYoga || '—'],
                    ].map(([label, value]) => (
                      <Grid key={label} size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(46,125,82,0.06)' }}>
                          <Typography variant="caption" color="text.secondary">
                            {label}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                            {value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {todayData?.events?.length ? (
                    <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                      {todayData.events.map((event) => (
                        <Box key={event._id} sx={{ p: 2, borderRadius: 2, border: '1px solid rgba(46,125,82,0.12)' }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {localizeTransitTitle(event, isHindi)}
                            </Typography>
                            {event.isMajor && <Chip size="small" color="error" label={t('transit.today.majorTransit')} />}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(event.eventDate)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {localizeTransitDescription(event, isHindi)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      {t('transit.today.noEvents')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <AutoAwesome color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('transit.personalized.title')}
                    </Typography>
                  </Stack>

                  {featuredImpact?.summary ? (
                    <Box sx={{ p: 2.25, borderRadius: 2.5, bgcolor: 'rgba(46,125,82,0.08)' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {localizeImpactText(featuredImpact.summary, isHindi)}
                      </Typography>
                      {featuredImpact.moonSummary && (
                        <Typography variant="body2" color="text.secondary">
                          {localizeImpactText(featuredImpact.moonSummary, isHindi)}
                        </Typography>
                      )}
                      {featuredImpact.chartName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                          {t('transit.personalized.basedOnSelectedKundli', { name: featuredImpact.chartName })}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ p: 2.25, borderRadius: 2.5, bgcolor: 'rgba(0,0,0,0.03)' }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Lock fontSize="small" color="action" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {t('transit.personalized.premiumInsight')}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {isPremium
                          ? t('transit.personalized.selectChartPrompt')
                          : t('transit.personalized.upgradePrompt')}
                      </Typography>
                      {!isPremium && (
                        <Button sx={{ mt: 2 }} variant="contained" href="/upgrade">
                          {t('transit.personalized.upgradeCta')}
                        </Button>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </>
      ) : tab === 'upcoming' ? (
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('transit.upcoming.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('transit.upcoming.subtitle')}
                </Typography>
              </Box>
              <Chip
                color="primary"
                variant="outlined"
                label={t('transit.upcoming.countLabel', { count: upcoming.length })}
              />
            </Stack>

            <Grid container spacing={2}>
              {upcoming.map((event) => (
                <Grid key={event._id} size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {localizeTransitTitle(event, isHindi)}
                        </Typography>
                        {event.isMajor && <Chip size="small" color="error" label={t('transit.upcoming.major')} />}
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        {formatDate(event.eventDate)}
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 1.25 }}>
                        {localizeTransitDescription(event, isHindi)}
                      </Typography>

                      {event.personalImpact?.summary && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(46,125,82,0.08)' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {localizeImpactText(event.personalImpact.summary, isHindi)}
                          </Typography>
                        </Box>
                      )}

                      {event.personalImpact?.locked && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.04)' }}>
                          <Typography variant="body2" color="text.secondary">
                            {localizeImpactText(event.personalImpact.message, isHindi)}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ) : tab === 'double' ? (
        isPremium ? (
          <DoubleTransitPanel
            chartId={id || currentChart?._id || currentChart?.chartId || null}
            chartData={currentChart}
            token={authState.token}
          />
        ) : (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>{t('doubleTransit.premiumTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>{t('doubleTransit.premiumMessage')}</Typography>
              <Button variant="contained" onClick={() => window.location.assign(authState.user ? '/pricing' : '/login')}>
                {authState.user ? t('common.upgrade') : t('doubleTransit.loginToUpgrade')}
              </Button>
            </CardContent>
          </Card>
        )
      ) : tab === 'nakshatra' ? (
        <NakshatraTransit apiBaseUrl={API_BASE_URL} studio />
      ) : (
        <Card>
          <CardContent>
            <CurrentTransit embedded />
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default GocharAlerts;
